import { useSearchParams, useNavigate } from "react-router";
import { useState, useEffect, useCallback } from "react";
import { Check, User, CreditCard, Users, ExternalLink, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { billing, type SubscriptionData } from "@/lib/api";

type TabId = "general" | "billing" | "affiliate";

const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: "general", label: "General", icon: User },
  { id: "billing", label: "Billing", icon: CreditCard },
  { id: "affiliate", label: "Affiliate", icon: Users },
];

type Currency = "usd" | "eur" | "brl";

const PRICE_IDS: Record<Currency, string> = {
  usd: "price_1T1GnoLlcvzNyOa2VBA95ndW",
  eur: "price_1T1GnpLlcvzNyOa2Ec0yODHS",
  brl: "price_1T1GnqLlcvzNyOa2VY3G9Gq4",
};

const PRICE_LABELS: Record<Currency, { symbol: string; amount: string }> = {
  usd: { symbol: "$", amount: "9" },
  eur: { symbol: "\u20ac", amount: "9" },
  brl: { symbol: "R$", amount: "49" },
};

const CURRENCY_OPTIONS: { id: Currency; label: string }[] = [
  { id: "usd", label: "USD" },
  { id: "eur", label: "EUR" },
  { id: "brl", label: "BRL" },
];

function GeneralTab() {
  const { user } = useAuth();

  const initials = (user?.displayName || user?.email || "?")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Profile</CardTitle>
        <CardDescription>Your account information is managed through your auth provider.</CardDescription>
      </CardHeader>
      <CardContent className="flex items-center gap-5">
        <Avatar className="h-16 w-16">
          <AvatarImage src={user?.photoURL || undefined} />
          <AvatarFallback className="text-lg">{initials}</AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          {user?.displayName && (
            <p className="text-sm font-medium truncate">{user.displayName}</p>
          )}
          <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function AffiliateTab() {
  const navigate = useNavigate();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Affiliate Program</CardTitle>
        <CardDescription>
          Earn commissions by referring new users to n8nvibes. Manage your affiliate profile, track referrals, and view payouts from the dedicated affiliate page.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button onClick={() => navigate("/affiliate")}>Go to Affiliate</Button>
      </CardContent>
    </Card>
  );
}

function BillingTab() {
  const [sub, setSub] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [currency, setCurrency] = useState<Currency>("usd");
  const [searchParams] = useSearchParams();

  const fetchSubscription = useCallback(async () => {
    try {
      const data = await billing.getSubscription();
      setSub(data);
    } catch {
      setSub({ planId: "free", status: null, currentPeriodEnd: null, cancelAtPeriodEnd: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const isIndividual = sub?.planId === "individual" && sub?.status === "active";
  const isCanceling = isIndividual && sub?.cancelAtPeriodEnd;

  const handleUpgrade = async () => {
    setCheckoutLoading(true);
    try {
      const baseUrl = window.location.origin + "/account?tab=billing";
      const data = await billing.createCheckout({
        priceId: PRICE_IDS[currency],
        successUrl: baseUrl + "&success=true",
        cancelUrl: baseUrl,
      });
      window.location.href = data.url;
    } catch (err) {
      console.error("Checkout error:", err);
      setCheckoutLoading(false);
    }
  };

  const handleManage = async () => {
    setPortalLoading(true);
    try {
      const data = await billing.createPortal({
        returnUrl: window.location.origin + "/account?tab=billing",
      });
      window.open(data.url, "_blank");
    } catch (err) {
      console.error("Portal error:", err);
    } finally {
      setPortalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const showSuccess = searchParams.get("success") === "true";
  const price = PRICE_LABELS[currency];

  const plans = [
    {
      name: "Free",
      description: "Get started with n8nvibes",
      price: `${PRICE_LABELS.usd.symbol}0`,
      period: "forever",
      features: ["1 project", "Community support", "Basic workflows"],
      isCurrent: !isIndividual,
      disabled: isIndividual,
      buttonLabel: isIndividual ? "Free" : "Current Plan",
    },
    {
      name: "Individual",
      description: "For power users",
      price: `${price.symbol}${price.amount}`,
      period: "/mo",
      features: ["Unlimited projects", "Priority support", "Advanced workflows", "Custom domains"],
      isCurrent: isIndividual,
      disabled: isIndividual,
      buttonLabel: isIndividual
        ? isCanceling ? "Canceling..." : "Current Plan"
        : "Upgrade",
      onAction: isIndividual ? undefined : handleUpgrade,
      actionLoading: checkoutLoading,
    },
    {
      name: "Teams",
      description: "For collaborative teams",
      price: "$49",
      period: "/mo",
      features: ["Everything in Individual", "Team workspaces", "Role-based access", "Audit logs"],
      isCurrent: false,
      disabled: true,
      buttonLabel: "Coming Soon",
    },
  ];

  return (
    <div className="space-y-6">
      {showSuccess && (
        <div className="rounded-lg border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm text-green-400">
          Payment successful! Your plan has been upgraded.
        </div>
      )}

      {/* Currency selector */}
      {!isIndividual && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Currency:</span>
          <div className="inline-flex rounded-md border border-border">
            {CURRENCY_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                onClick={() => setCurrency(opt.id)}
                className={cn(
                  "px-3 py-1 text-xs font-medium transition-colors first:rounded-l-md last:rounded-r-md",
                  currency === opt.id
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted"
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-3">
        {plans.map((plan) => (
          <Card key={plan.name} className={cn(plan.isCurrent && "border-primary")}>
            <CardHeader>
              <CardTitle>{plan.name}</CardTitle>
              <CardDescription>{plan.description}</CardDescription>
              <div className="pt-2">
                <span className="text-3xl font-bold">{plan.price}</span>
                <span className="text-sm text-muted-foreground">{plan.period}</span>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 mb-6">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-primary shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <Button
                className="w-full"
                variant={plan.isCurrent ? "outline" : "default"}
                disabled={plan.disabled || plan.actionLoading}
                onClick={plan.onAction}
              >
                {plan.actionLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                {plan.buttonLabel}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {isIndividual && (
        <Button variant="outline" size="sm" onClick={handleManage} disabled={portalLoading}>
          {portalLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ExternalLink className="h-4 w-4 mr-2" />}
          Manage Subscription
        </Button>
      )}

      {sub?.currentPeriodEnd && (
        <p className="text-xs text-muted-foreground">
          {isCanceling
            ? `Your plan will be downgraded to Free on ${new Date(sub.currentPeriodEnd).toLocaleDateString()}.`
            : `Next billing date: ${new Date(sub.currentPeriodEnd).toLocaleDateString()}`}
        </p>
      )}
    </div>
  );
}

export function AccountPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get("tab") as TabId) || "general";

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-2xl font-semibold mb-6">Account</h1>

      <div className="flex flex-col md:flex-row gap-6">
        {/* Vertical tab sidebar */}
        <nav className="md:w-48 shrink-0 flex md:flex-col gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setSearchParams({ tab: tab.id })}
              className={cn(
                "flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors text-left",
                activeTab === tab.id
                  ? "bg-accent text-accent-foreground"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>

        {/* Tab content */}
        <div className="flex-1 min-w-0">
          {activeTab === "general" && <GeneralTab />}
          {activeTab === "billing" && <BillingTab />}
          {activeTab === "affiliate" && <AffiliateTab />}
        </div>
      </div>
    </div>
  );
}
